import { gsap } from 'gsap';
import { prefersReducedMotion } from '@/effects/motion';

/**
 * Dramatic entrance animation for the hero section elements.
 * Called once after translations have been applied to the DOM.
 *
 * Timeline:
 *  0.0s  – three logo lines drop in with staggered rotation
 *  0.45s – hero copy fades + slides up
 *  0.75s – CTA button scales in
 *  0.85s – scroll indicator fades in
 */
export const initHeroEntrance = (): void => {
  if (prefersReducedMotion()) return;

  const tl = gsap.timeline({ defaults: { ease: 'power3.out' } });

  // ── Logo lines (DEV / ME / UP) ──
  const logoLines = document.querySelectorAll<HTMLElement>('.logo-home');
  if (logoLines.length) {
    gsap.set(logoLines, { opacity: 0, y: -60, rotateZ: 0 });
    tl.to(
      logoLines,
      {
        opacity: 1,
        y: 0,
        duration: 0.8,
        stagger: 0.12,
      },
      0
    );
  }

  // ── Hero copy block ──
  const heroCopy = document.querySelector<HTMLElement>('.hero-copy');
  if (heroCopy) {
    gsap.set(heroCopy, { opacity: 0, y: 40 });
    tl.to(heroCopy, { opacity: 1, y: 0, duration: 0.9 }, 0.45);
  }

  // ── CTA button ──
  const cta = document.querySelector<HTMLElement>('.hero-cta');
  if (cta) {
    gsap.set(cta, { opacity: 0, scale: 0.8 });
    tl.to(cta, { opacity: 1, scale: 1, duration: 0.6, ease: 'back.out(1.8)' }, 0.75);
  }

  // ── Scroll indicator ──
  const mouse = document.querySelector<HTMLElement>('.mouse-container');
  if (mouse) {
    gsap.set(mouse, { opacity: 0, y: 12 });
    tl.to(mouse, { opacity: 1, y: 0, duration: 0.6 }, 0.9);
  }
};

/**
 * Scrambles text characters and resolves to the final string.
 * Used for the hero title on first load for a "glitch" feel.
 */
const CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*<>/?';

export const scrambleIn = (el: HTMLElement, duration = 1.4): void => {
  if (prefersReducedMotion()) return;

  const finalText = el.textContent ?? '';
  if (!finalText.trim()) return;

  const chars = finalText.split('');
  const maxFrames = Math.round(duration * 60);
  let frame = 0;

  const update = (): void => {
    const progress = frame / maxFrames;
    const resolvedCount = Math.floor(progress * chars.length);

    el.textContent = chars
      .map((ch, i) => {
        if (i < resolvedCount || ch === ' ') return ch;
        return CHARS[Math.floor(Math.random() * CHARS.length)];
      })
      .join('');

    frame++;
    if (frame <= maxFrames) {
      requestAnimationFrame(update);
    } else {
      el.textContent = finalText;
    }
  };

  update();
};
